import React, { useEffect, useMemo, useRef, useState } from "react";
import Map from "react-map-gl/maplibre";
import maplibregl from "maplibre-gl";

import * as turf from "@turf/turf";

import "maplibre-gl/dist/maplibre-gl.css";
import { RatingStars } from "./RatingStars";
import { geocodeQuery, hasCoords } from "./geocode";

const MAP_STYLE = `https://api.maptiler.com/maps/streets/style.json?key=${process.env.REACT_APP_MAPTILER_KEY}`;

const milesToMeters = (m) => Number(m) * 1609.344;

// Drops records without usable coordinates (see hasCoords).
export function normalizeEntities(raw) {
  return (raw || [])
    .filter((e) => hasCoords(e.lat, e.lng))
    .map((e) => {
      const radiusMiles = Number(e.radius_miles) || 50;

      return {
        ...e,
        id: e.id,
        name: e.name,
        lat: Number(e.lat),
        lng: Number(e.lng),
        radius_miles: radiusMiles,
        radius_meters: milesToMeters(radiusMiles),
        type: e.type || "investigator",
      };
    });
}

function makePointsGeoJSON(entities, filters) {
  const { showInvestigators, showProcessors, matchIds } = filters;

  const features = entities
    .filter((e) => {
      if (e.type === "investigator" && !showInvestigators) return false;
      if (e.type === "processor" && !showProcessors) return false;
      return true;
    })
    .map((e) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [e.lng, e.lat] },
      properties: {
        id: e.id,
        type: e.type,
        name: e.name,
        address: e.address || "",
        phone: e.phone || "",
        email: e.email || "",
        hourly_rate: e.hourly_rate || "",
        radius_miles: e.radius_miles,
        matched: matchIds.has(e.id) ? 1 : 0,
        average_rating: e.average_rating || 0,
      },
    }));

  return {
    type: "FeatureCollection",
    features,
  };
}

function makeCoverageGeoJSON(entities, filters) {
  const { showInvestigators, showProcessors, matchIds } = filters;

  return {
    type: "FeatureCollection",
    features: entities
      .filter((e) => {
        if (e.type === "investigator" && !showInvestigators) return false;
        if (e.type === "processor" && !showProcessors) return false;
        return true;
      })
      .map((e) =>
        turf.circle([e.lng, e.lat], e.radius_meters / 1000, {
          steps: 64,
          units: "kilometers",
          properties: {
            id: e.id,
            type: e.type,
            name: e.name,
            radius_miles: e.radius_miles,
            matched: matchIds.has(e.id) ? 1 : 0,
          },
        })
      ),
  };
}

function makeSearchGeoJSON(searchPoint) {
  if (!searchPoint) {
    return { type: "FeatureCollection", features: [] };
  }
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [searchPoint.lng, searchPoint.lat],
        },
        properties: { label: searchPoint.label || "Search" },
      },
    ],
  };
}

function computeCoverageMatches(entities, lng, lat) {
  const p = turf.point([lng, lat]);
  const matched = new Set();

  for (const e of entities) {
    const poly = turf.circle([e.lng, e.lat], e.radius_meters / 1000, {
      steps: 64,
      units: "kilometers",
    });
    if (turf.booleanPointInPolygon(p, poly)) {
      matched.add(e.id);
    }
  }

  return matched;
}

const EMPTY_SET = new Set();

const LAYERS = {
  coverageFill: {
    id: "coverage-fill",
    type: "fill",
    source: "coverage",
    paint: {
      "fill-color": [
        "case",
        ["==", ["get", "type"], "investigator"],
        "rgba(45, 125, 255, 0.18)",
        "rgba(45, 200, 120, 0.18)",
      ],
      "fill-opacity": ["case", ["==", ["get", "matched"], 1], 0.35, 0.14],
    },
  },
  coverageLine: {
    id: "coverage-line",
    type: "line",
    source: "coverage",
    paint: {
      "line-width": ["case", ["==", ["get", "matched"], 1], 2.5, 1.2],
      "line-color": [
        "case",
        ["==", ["get", "type"], "investigator"],
        "rgba(45, 125, 255, 0.6)",
        "rgba(45, 200, 120, 0.6)",
      ],
      "line-opacity": ["case", ["==", ["get", "matched"], 1], 0.9, 0.55],
    },
  },
  points: {
    id: "entities-points",
    type: "circle",
    source: "entities",
    paint: {
      "circle-radius": ["case", ["==", ["get", "matched"], 1], 9, 6],
      "circle-color": [
        "case",
        ["==", ["get", "type"], "investigator"],
        "rgba(45, 125, 255, 1)",
        "rgba(45, 200, 120, 1)",
      ],
      "circle-stroke-width": 2,
      "circle-stroke-color": [
        "case",
        ["==", ["get", "matched"], 1],
        "#111",
        "#fff",
      ],
    },
  },
  labels: {
    id: "entities-labels",
    type: "symbol",
    source: "entities",
    layout: {
      "text-field": ["get", "name"],
      "text-size": 12,
      "text-offset": [0, 1.2],
      "text-anchor": "top",
    },
    paint: {
      "text-color": "#111",
      "text-halo-color": "rgba(255,255,255,0.95)",
      "text-halo-width": 1.2,
    },
  },
  searchPoint: {
    id: "search-point",
    type: "circle",
    source: "search",
    paint: {
      "circle-radius": 7,
      "circle-color": "rgba(0,0,0,0.92)",
      "circle-stroke-width": 3,
      "circle-stroke-color": "rgba(255,255,255,1)",
    },
  },
};

// Shared "Add as Objective" button used in both the results panel and the selected-entity card.
function AssignButton({ id, name, alreadyAdded, onAssign, fullWidth = false }) {
  return (
    <button
      onClick={() => { if (!alreadyAdded) onAssign(id, name); }}
      disabled={alreadyAdded}
      style={{
        ...(fullWidth ? { marginTop: 12, width: "100%" } : { flexShrink: 0 }),
        fontSize: fullWidth ? 12 : 11,
        fontWeight: 600,
        padding: fullWidth ? "7px 12px" : "4px 10px",
        borderRadius: fullWidth ? 7 : 6,
        border: "none",
        cursor: alreadyAdded ? "default" : "pointer",
        background: alreadyAdded ? "#f0fdf4" : "#004B5D",
        color: alreadyAdded ? "#16a34a" : "white",
        transition: "background 0.15s ease",
      }}
    >
      {alreadyAdded ? "Added ✓" : (fullWidth ? "Add as Objective" : "Add")}
    </button>
  );
}

/**
 * Props:
 *   fieldResourceList  – array of FieldResource objects
 *   onAssign(id, name) – optional; when provided the map renders in "picker" mode
 *                        (dialog-embedded). An "Add as Objective" button appears in
 *                        the selected-entity card and in the coverage results panel.
 *   assignedIds        – Set<string>; IDs already added as objectives. Button is
 *                        disabled (shows "Added ✓") for these resources.
 *   mapHeight          – CSS height string for the outer container.
 *                        Defaults to "100vh" (full-page). Pass e.g. "420px" for
 *                        dialog/embedded use.
 */
export default function FieldResourceCoverageMap({
  fieldResourceList = [],
  onAssign,
  assignedIds = EMPTY_SET,
  mapHeight,
}) {
  const embedded = Boolean(onAssign);
  const mapRef = useRef(null);

  const entities = useMemo(
    () => normalizeEntities(fieldResourceList),
    [fieldResourceList]
  );

  const [showInvestigators, setShowInvestigators] = useState(true);
  const [showProcessors, setShowProcessors] = useState(true);
  const [showCoverage, setShowCoverage] = useState(true);

  const [query, setQuery] = useState("");
  const [searchError, setSearchError] = useState("");
  const [searchPoint, setSearchPoint] = useState(null);
  const [matchIds, setMatchIds] = useState(new Set());

  const [hoverInfo, setHoverInfo] = useState(null);
  const [selectedInfo, setSelectedInfo] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const filters = useMemo(
    () => ({ showInvestigators, showProcessors, matchIds }),
    [showInvestigators, showProcessors, matchIds]
  );

  const pointsGeoJSON = useMemo(
    () => makePointsGeoJSON(entities, filters),
    [entities, filters]
  );

  const coverageGeoJSON = useMemo(
    () => makeCoverageGeoJSON(entities, filters),
    [entities, filters]
  );

  const searchGeoJSON = useMemo(
    () => makeSearchGeoJSON(searchPoint),
    [searchPoint]
  );

  const matchedEntities = useMemo(() => {
    const arr = entities.filter((e) => matchIds.has(e.id));
    return arr.sort((a, b) =>
      a.type === b.type
        ? a.name.localeCompare(b.name)
        : a.type === "investigator"
        ? -1
        : 1
    );
  }, [entities, matchIds]);

  function ensureSourcesAndLayers(map) {
    if (!coverageGeoJSON || !pointsGeoJSON) return;

    // Sources
    if (!map.getSource("coverage")) {
      map.addSource("coverage", {
        type: "geojson",
        data: coverageGeoJSON,
      });
    }

    if (!map.getSource("entities")) {
      map.addSource("entities", {
        type: "geojson",
        data: pointsGeoJSON,
      });
    }

    if (!map.getSource("search") && searchGeoJSON) {
      map.addSource("search", {
        type: "geojson",
        data: searchGeoJSON,
      });
    }

    // Layers
    if (showCoverage) {
      if (!map.getLayer(LAYERS.coverageFill.id))
        map.addLayer(LAYERS.coverageFill);
      if (!map.getLayer(LAYERS.coverageLine.id))
        map.addLayer(LAYERS.coverageLine);
    }

    if (!map.getLayer(LAYERS.points.id)) {
      map.addLayer(LAYERS.points);
    }

    const hasGlyphs = !!map.getStyle()?.glyphs;
    if (hasGlyphs && !map.getLayer(LAYERS.labels.id)) {
      map.addLayer(LAYERS.labels);
    }

    if (searchGeoJSON && !map.getLayer(LAYERS.searchPoint.id)) {
      map.addLayer(LAYERS.searchPoint);
    }
  }

  useEffect(() => {
    if (!mapRef.current) return;
    ensureSourcesAndLayers(mapRef.current);
  }, [coverageGeoJSON, pointsGeoJSON, searchGeoJSON, showCoverage]);

  useEffect(() => {
    const map = mapRef.current?.getMap?.();
    if (!map) return;
    const src = map.getSource("entities");
    if (src && src.setData) src.setData(pointsGeoJSON);
  }, [pointsGeoJSON]);

  useEffect(() => {
    const map = mapRef.current?.getMap?.();
    if (!map) return;
    const src = map.getSource("coverage");
    if (src && src.setData) src.setData(coverageGeoJSON);
  }, [coverageGeoJSON]);

  useEffect(() => {
    const map = mapRef.current?.getMap?.();
    if (!map) return;
    const src = map.getSource("search");
    if (src && src.setData) src.setData(searchGeoJSON);
  }, [searchGeoJSON]);

  useEffect(() => {
    const map = mapRef.current?.getMap?.();
    if (!map) return;

    // Toggle visibility instead of removing/adding layers
    const visibility = showCoverage ? "visible" : "none";

    if (map.getLayer(LAYERS.coverageFill.id)) {
      map.setLayoutProperty(LAYERS.coverageFill.id, "visibility", visibility);
    }
    if (map.getLayer(LAYERS.coverageLine.id)) {
      map.setLayoutProperty(LAYERS.coverageLine.id, "visibility", visibility);
    }
  }, [showCoverage]);

  async function runSearch(raw) {
    const q = String(raw || "").trim();
    if (!q) return;

    setSearchError("");

    const geo = await geocodeQuery(q);
    if (!geo) {
      setSearchError("No results found");
      setSearchPoint(null);
      setMatchIds(new Set());
      return;
    }

    const matched = computeCoverageMatches(entities, geo.lng, geo.lat);
    setSearchPoint({ lng: geo.lng, lat: geo.lat, label: geo.label });
    setMatchIds(matched);
    if (matched.size > 0) {
      setShowResults(true);
    }

    const map = mapRef.current?.getMap?.();
    if (map) map.flyTo({ center: [geo.lng, geo.lat], zoom: 10, speed: 1.25 });
  }

  return (
    <div
      style={{
        height: mapHeight ?? (embedded ? "100%" : "100vh"),
        width: "100%",
        position: "relative",
      }}
    >
      <Map
        ref={mapRef}
        mapLib={maplibregl}
        initialViewState={{
          longitude: -95.7129,
          latitude: 37.0902,
          zoom: 4,
        }}
        style={{
          width: "100%",
          height: embedded ? "100%" : "78%",
          borderRadius: 10,
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
        mapStyle={MAP_STYLE}
        interactiveLayerIds={["entities-points"]}
        onError={(e) => {
          console.error("Map error:", e);
        }}
        onLoad={(evt) => {
          console.log("Map loaded successfully!");
          const map = evt.target;
          ensureSourcesAndLayers(map);
        }}
        onMouseMove={(evt) => {
          const f = evt.features && evt.features[0];
          if (!f || f.layer?.id !== "entities-points") {
            setHoverInfo(null);
            return;
          }
          setHoverInfo({
            lng: evt.lngLat.lng,
            lat: evt.lngLat.lat,
            properties: f.properties,
          });
        }}
        onClick={(evt) => {
          console.log(evt);
          const f = evt.features && evt.features[0];
          if (f && f.layer?.id === "entities-points") {
            setSelectedInfo({
              lng: evt.lngLat.lng,
              lat: evt.lngLat.lat,
              properties: f.properties,
            });
          } else {
            setSelectedInfo(null);
          }
        }}
      >
        {/* Search Bar - Top Center */}
        <div
          style={{
            position: "absolute",
            top: 20,
            left: embedded ? "50%" : (showResults || matchedEntities.length > 0 ? "18vw" : "16vw"),
            transform: "translateX(-50%)",
            zIndex: 10,
            display: "flex",
            gap: 8,
            alignItems: "center",
          }}
        >
          <div
            style={{
              background: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(10px)",
              borderRadius: 12,
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              display: "flex",
              alignItems: "center",
              padding: "8px 12px",
              gap: 8,
              minWidth: 400,
            }}
          >
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search ZIP or address..."
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                background: "transparent",
                fontSize: 14,
                padding: "4px 8px",
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") void runSearch(query);
              }}
            />
            <button
              onClick={() => void runSearch(query)}
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border: "none",
                background: "#004B5D",
                color: "white",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              Search
            </button>
          </div>

          {matchedEntities.length > 0 && (
            <button
              onClick={() => setShowResults(!showResults)}
              style={{
                padding: "10px 16px",
                borderRadius: 12,
                border: "none",
                background: "rgba(255,255,255,0.95)",
                backdropFilter: "blur(10px)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 13,
                color: "#004B5D",
              }}
            >
              {matchedEntities.length} Found
            </button>
          )}
        </div>

        {/* Error Message */}
        {searchError && (
          <div
            style={{
              position: "absolute",
              top: 80,
              left: embedded ? "50%" : "18%",
              transform: "translateX(-50%)",
              background: "rgba(220, 38, 38, 0.95)",
              color: "white",
              padding: "10px 16px",
              borderRadius: 8,
              fontSize: 12,
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            }}
          >
            {searchError}
          </div>
        )}

        {/* Results Panel - Slides from top */}
        {showResults && matchedEntities.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: 80,
              ...(embedded
                ? { left: 12 }
                : { left: "14.25vw", transform: "translateX(-50%)" }),
              width: embedded ? 340 : 380,
              maxHeight: embedded ? 380 : "60vh",
              background: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(10px)",
              borderRadius: 12,
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
              overflow: "hidden",
              zIndex: 10,
            }}
          >
            <div
              style={{
                padding: "12px 16px",
                borderBottom: "1px solid rgba(0,0,0,0.1)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 13 }}>
                Coverage Results
              </span>
              <button
                onClick={() => setShowResults(false)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 18,
                  color: "#666",
                }}
              >
                ×
              </button>
            </div>
            <div
              style={{
                maxHeight: embedded ? "calc(380px - 50px)" : "calc(60vh - 50px)",
                overflowY: "auto",
                padding: 12,
              }}
            >
              {matchedEntities.map((e) => {
                const alreadyAdded = assignedIds.has(e.id);
                return (
                  <div
                    key={e.id}
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      padding: 12,
                      marginBottom: 8,
                      borderRadius: 8,
                      border: "1px solid rgba(0,0,0,0.08)",
                      background: "white",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <button
                      onClick={() => {
                        const map = mapRef.current?.getMap?.();
                        if (map)
                          map.flyTo({
                            center: [e.lng, e.lat],
                            zoom: 10,
                            speed: 1.3,
                          });
                        setSelectedInfo({
                          lng: e.lng,
                          lat: e.lat,
                          properties: { ...e, matched: 1 },
                        });
                        setShowResults(false);
                      }}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        textAlign: "left",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          marginBottom: 3,
                          overflow: "hidden",
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 700,
                            fontSize: 13,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {e.name}
                        </span>
                        <span style={{ flexShrink: 0 }}>
                          <RatingStars value={e.average_rating} />
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "#666",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {e.address || "No address"} • {e.radius_miles} mi
                      </div>
                    </button>
                    {embedded && (
                      <AssignButton
                        id={e.id}
                        name={e.name}
                        alreadyAdded={alreadyAdded}
                        onAssign={onAssign}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Filters - Top Right */}
        <div
          style={{
            position: "absolute",
            top: 20,
            right: 20,
            background: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(10px)",
            borderRadius: 12,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            padding: "12px 16px",
            zIndex: 10,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              marginBottom: 10,
              color: "#666",
            }}
          >
            LAYERS
          </div>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            <input
              type="checkbox"
              checked={showInvestigators}
              onChange={(e) => setShowInvestigators(e.target.checked)}
              style={{ cursor: "pointer" }}
            />
            <span>Investigators</span>
          </label>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            <input
              type="checkbox"
              checked={showProcessors}
              onChange={(e) => setShowProcessors(e.target.checked)}
              style={{ cursor: "pointer" }}
            />
            <span>Processors</span>
          </label>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            <input
              type="checkbox"
              checked={showCoverage}
              onChange={(e) => setShowCoverage(e.target.checked)}
              style={{ cursor: "pointer" }}
            />
            <span>Coverage Areas</span>
          </label>
        </div>

        {/* Legend - Bottom Right */}
        <div
          style={{
            position: "absolute",
            bottom: 42,
            right: 12,
            background: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(10px)",
            borderRadius: 12,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            padding: "12px 16px",
            fontSize: 11,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 8, color: "#666" }}>
            LEGEND
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 6,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                background: "rgba(45,125,255,1)",
              }}
            />
            <span>Investigator</span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 6,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                background: "rgba(45,200,120,1)",
              }}
            />
            <span>Processor</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                background: "rgba(0,0,0,0.9)",
              }}
            />
            <span>Search Point</span>
          </div>
        </div>

        {/* Hover Tooltip */}
        {hoverInfo && (
          <div
            style={{
              position: "absolute",
              left: 12,
              bottom: 12,
              background: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(10px)",
              borderRadius: 10,
              padding: "10px 12px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              fontSize: 11,
              maxWidth: 280,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              {hoverInfo.properties?.name}
            </div>
            <div style={{ color: "#666" }}>
              {hoverInfo.properties?.type} •{" "}
              {hoverInfo.properties?.address || "No address"}
            </div>
          </div>
        )}

        {/* Selected Entity Card */}
        {selectedInfo && (
          <div
            style={{
              position: "absolute",
              left: 12,
              top: embedded ? 70 : "10vh",
              background: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(10px)",
              borderRadius: 12,
              padding: "14px 16px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
              fontSize: 12,
              maxWidth: embedded ? 260 : 320,
              zIndex: 20,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "start",
                marginBottom: 8,
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                {selectedInfo.properties?.name}
              </div>
              <button
                onClick={() => setSelectedInfo(null)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 20,
                  color: "#666",
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                ×
              </button>
            </div>
            <div style={{ color: "#666", marginBottom: 8 }}>
              {selectedInfo.properties?.address || "No address"} •{" "}
              {selectedInfo.properties?.radius_miles} mi
            </div>
            {selectedInfo.properties?.phone && (
              <div style={{ marginBottom: 4, color: "#666" }}>
                {selectedInfo.properties.phone}
              </div>
            )}
            {selectedInfo.properties?.email && (
              <div style={{ marginBottom: 8, color: "#666" }}>
                {selectedInfo.properties.email}
              </div>
            )}
            {selectedInfo.properties?.average_rating && (
              <div
                style={{
                  marginBottom: 8,
                  color: "#666",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <RatingStars value={selectedInfo.properties.average_rating} />
              </div>
            )}

            {Number(selectedInfo.properties?.matched) === 1 && (
              <div
                style={{
                  marginTop: 10,
                  padding: "6px 10px",
                  background: "rgba(34, 197, 94, 0.1)",
                  borderRadius: 6,
                  color: "#16a34a",
                  fontWeight: 600,
                  fontSize: 11,
                }}
              >
                ✓ Covers searched location
              </div>
            )}

            {embedded && (
              <AssignButton
                id={String(selectedInfo.properties?.id ?? "")}
                name={String(selectedInfo.properties?.name ?? "")}
                alreadyAdded={Boolean(selectedInfo.properties?.id && assignedIds.has(String(selectedInfo.properties.id)))}
                onAssign={onAssign}
                fullWidth
              />
            )}
          </div>
        )}
      </Map>
    </div>
  );
}
